<div class="upload-inspection-admin-container">
  <div class="header">
    <h2>Upload Inspection Requests</h2>
    <button class="btn btn-primary" (click)="onRefresh()" [disabled]="isLoading">
      <i class="fa fa-refresh"></i> Refresh
    </button>
  </div>

  <div class="search-bar">
    <div class="search-input-group">
      <input
        type="text"
        [(ngModel)]="searchUploadId"
        placeholder="Search by Upload ID..."
        (keyup.enter)="onSearch()"
        class="search-input"
      />
      <button class="btn btn-search" (click)="onSearch()" [disabled]="isLoading">
        <i class="fa fa-search"></i> Search
      </button>
      <button class="btn btn-clear" (click)="onClearSearch()" [disabled]="isLoading">
        <i class="fa fa-times"></i> Clear
      </button>
    </div>
  </div>

  <div class="info-bar">
    <span>Total Records: {{ recordCount }}</span>
    <span class="separator">|</span>
    <span>Current Page: {{ currentPage + 1 }} of {{ pageCount }}</span>
  </div>

  <div class="loader-container" *ngIf="isLoading">
    <div class="spinner"></div>
    <p>Loading upload inspection requests...</p>
  </div>

  <div class="datatable-wrapper" *ngIf="!isLoading">
    <table class="table table-striped table-hover">
      <thead>
        <tr>
          <th *ngFor="let col of columns" (click)="onSort(col.name)" [class.sortable]="col.sortable">
            {{ col.label }}
            <span class="sort-indicator" *ngIf="sortColumn === col.name">
              <i class="fa" [ngClass]="sortDescending ? 'fa-chevron-down' : 'fa-chevron-up'"></i>
            </span>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr *ngFor="let row of rows" [class.error-row]="row.hasError">
          <!-- Upload Id -->
          <td>{{ row.uploadId }}</td>

          <!-- Requested On -->
          <td>{{ formatDate(row.requestedOn) }}</td>

          <!-- User -->
          <td>{{ row.requestedByUserName || 'N/A' }}</td>

          <!-- Inspection Type -->
          <td>{{ row.inspectionType || 'N/A' }}</td>

          <!-- ScheduledInspectionId -->
          <td>{{ getScheduledInspectionIdDisplay(row) }}</td>

          <!-- Application Number -->
          <td>{{ getApplicationNumberDisplay(row) }}</td>

          <!-- Attachments -->
          <td>
            <button
              *ngIf="getAttachmentsDisplay(row) === 'View'"
              class="btn btn-link"
              (click)="openAttachmentsModal(row)"
            >
              <i class="fa fa-paperclip"></i> View
            </button>
            <span *ngIf="getAttachmentsDisplay(row) === 'None'" class="text-muted">None</span>
          </td>

          <!-- Signatures -->
          <td>
            <button
              *ngIf="getSignaturesDisplay(row) === 'View'"
              class="btn btn-link"
              (click)="openSignaturesModal(row)"
            >
              <i class="fa fa-image"></i> View
            </button>
            <span *ngIf="getSignaturesDisplay(row) === 'None'" class="text-muted">None</span>
          </td>

          <!-- Upload Request Details -->
          <td>
            <button class="btn btn-link" (click)="openDetailsModal(row)">
              <i class="fa fa-info-circle"></i> View Details
            </button>
          </td>

          <!-- Processed Application -->
          <td>
            <a *ngIf="row.processedApplicationUrl" [href]="row.processedApplicationUrl" class="btn btn-link" target="_blank">
              <i class="fa fa-external-link"></i> View Application
            </a>
            <span *ngIf="!row.processedApplicationUrl" class="text-muted">N/A</span>
          </td>

          <!-- Status -->
          <td>
            <button
              *ngIf="row.requestStatus === 'Completed'"
              class="status-badge status-completed btn-status"
              (click)="openResultsModal(row)"
              title="Click to view results"
            >
              {{ row.requestStatus }}
            </button>
            <button
              *ngIf="row.requestStatus === 'Error'"
              class="status-badge status-error btn-status"
              (click)="openErrorModal(row)"
              title="Click to view error"
            >
              {{ row.requestStatus }}
            </button>
            <span
              *ngIf="row.requestStatus !== 'Completed' && row.requestStatus !== 'Error'"
              class="status-badge"
              [ngClass]="getStatusClass(row.requestStatus)"
            >
              {{ row.requestStatus }}
            </span>
          </td>
        </tr>
      </tbody>
    </table>

    <div *ngIf="rows.length === 0" class="no-data">
      <p>No upload inspection requests found.</p>
    </div>
  </div>

  <div class="pagination-footer">
    <button [disabled]="currentPage === 0" (click)="onPageChange(currentPage - 1)" class="btn btn-sm">
      <i class="fa fa-chevron-left"></i> Previous
    </button>
    <span class="page-info">Page {{ currentPage + 1 }} of {{ pageCount }}</span>
    <button [disabled]="currentPage >= pageCount - 1" (click)="onPageChange(currentPage + 1)" class="btn btn-sm">
      Next <i class="fa fa-chevron-right"></i>
    </button>
  </div>

  <!-- Attachments Modal -->
  <div class="modal" [class.show]="showAttachmentsModal" *ngIf="showAttachmentsModal">
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Attachments</h5>
          <button type="button" class="btn-close" (click)="closeAttachmentsModal()"></button>
        </div>
        <div class="modal-body">
          <div *ngIf="selectedAttachments && selectedAttachments.length > 0; else noAttachments">
            <div *ngFor="let attachment of selectedAttachments" class="attachment-item">
              <a [href]="attachment.Path" target="_blank" class="attachment-link">
                <i class="fa fa-download"></i> {{ attachment.Name }}
              </a>
            </div>
          </div>
          <ng-template #noAttachments>
            <p class="text-muted">No attachments found.</p>
          </ng-template>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" (click)="closeAttachmentsModal()">Close</button>
        </div>
      </div>
    </div>
  </div>

  <!-- Signatures Modal -->
  <div class="modal" [class.show]="showSignaturesModal" *ngIf="showSignaturesModal">
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Signatures</h5>
          <button type="button" class="btn-close" (click)="closeSignaturesModal()"></button>
        </div>
        <div class="modal-body">
          <div *ngIf="selectedSignatures && selectedSignatures.length > 0; else noSignatures">
            <div *ngFor="let signature of selectedSignatures" class="signature-item">
              <a [href]="signature.Path" target="_blank" class="signature-link">
                <i class="fa fa-download"></i> {{ signature.Name }}
              </a>
            </div>
          </div>
          <ng-template #noSignatures>
            <p class="text-muted">No signatures found.</p>
          </ng-template>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" (click)="closeSignaturesModal()">Close</button>
        </div>
      </div>
    </div>
  </div>

  <!-- Details Modal -->
  <div class="modal" [class.show]="showDetailsModal" *ngIf="showDetailsModal">
    <div class="modal-dialog modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Upload Request Details</h5>
          <button type="button" class="btn-close" (click)="closeDetailsModal()"></button>
        </div>
        <div class="modal-body" *ngIf="selectedDetails">
          <div class="details-grid">
            <div class="detail-row">
              <label>Upload ID:</label>
              <span>{{ selectedDetails.uploadId }}</span>
            </div>
            <div class="detail-row">
              <label>Requested On:</label>
              <span>{{ formatDate(selectedDetails.requestedOn) }}</span>
            </div>
            <div class="detail-row">
              <label>Requested By User ID:</label>
              <span>{{ selectedDetails.requestedByUserId || 'N/A' }}</span>
            </div>
            <div class="detail-row">
              <label>Requested By User Name:</label>
              <span>{{ selectedDetails.requestedByUserName || 'N/A' }}</span>
            </div>
            <div class="detail-row">
              <label>Collected On:</label>
              <span>{{ formatDate(selectedDetails.collectedOn) || 'Not collected' }}</span>
            </div>
            <div class="detail-row">
              <label>Workflow Version ID:</label>
              <span>{{ selectedDetails.workflowVersionId }}</span>
            </div>
            <div class="detail-row">
              <label>Inspection Type:</label>
              <span>{{ selectedDetails.inspectionType }}</span>
            </div>
            <div class="detail-row">
              <label>No Schedule Inspection:</label>
              <span>{{ selectedDetails.noScheduleInspection ? 'Yes' : 'No' }}</span>
            </div>
            <div class="detail-row">
              <label>Scheduled Inspection ID:</label>
              <span>{{ selectedDetails.scheduledInspectionId || 'N/A' }}</span>
            </div>
            <div class="detail-row">
              <label>Workflow Application ID:</label>
              <span>{{ selectedDetails.workflowApplicationId || 'N/A' }}</span>
            </div>
            <div class="detail-row">
              <label>Application Number:</label>
              <span>{{ selectedDetails.applicationNumber || 'N/A' }}</span>
            </div>
            <div class="detail-row">
              <label>Status:</label>
              <span
                class="status-badge"
                [ngClass]="getStatusClass(selectedDetails.requestStatus)"
              >
                {{ selectedDetails.requestStatus }}
              </span>
            </div>
          </div>

          <!-- JSON Sections -->
          <div class="json-sections">
            <div class="json-section" *ngIf="selectedDetails.formValuesJSON">
              <label>Form Values JSON:</label>
              <pre class="json-content">{{ selectedDetails.formValuesJSON }}</pre>
            </div>

            <div class="json-section" *ngIf="selectedDetails.attachmentsJSON">
              <label>Attachments JSON:</label>
              <pre class="json-content">{{ selectedDetails.attachmentsJSON }}</pre>
            </div>

            <div class="json-section" *ngIf="selectedDetails.inspectorSignatureImageJSON">
              <label>Inspector Signature Image JSON:</label>
              <pre class="json-content">{{ selectedDetails.inspectorSignatureImageJSON }}</pre>
            </div>

            <div class="json-section" *ngIf="selectedDetails.picSignatureImageJSON">
              <label>PIC Signature Image JSON:</label>
              <pre class="json-content">{{ selectedDetails.picSignatureImageJSON }}</pre>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" (click)="closeDetailsModal()">Close</button>
        </div>
      </div>
    </div>
  </div>

  <!-- Results Modal -->
  <div class="modal" [class.show]="showResultsModal" *ngIf="showResultsModal">
    <div class="modal-dialog modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Processing Results</h5>
          <button type="button" class="btn-close" (click)="closeResultsModal()"></button>
        </div>
        <div class="modal-body">
          <pre class="results-content">{{ selectedResults | json }}</pre>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" (click)="closeResultsModal()">Close</button>
        </div>
      </div>
    </div>
  </div>

  <!-- Error Modal -->
  <div class="modal" [class.show]="showErrorModal" *ngIf="showErrorModal">
    <div class="modal-dialog modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Error Message</h5>
          <button type="button" class="btn-close" (click)="closeErrorModal()"></button>
        </div>
        <div class="modal-body">
          <pre class="error-content">{{ selectedError }}</pre>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" (click)="closeErrorModal()">Close</button>
        </div>
      </div>
    </div>
  </div>

  <!-- Modal Backdrop -->
  <div class="modal-backdrop" [class.show]="showAttachmentsModal || showSignaturesModal || showDetailsModal || showResultsModal || showErrorModal"
    *ngIf="showAttachmentsModal || showSignaturesModal || showDetailsModal || showResultsModal || showErrorModal"></div>
</div>

